import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  StaffMemberSettings,
  StaffMemberSettingsUpdatedEnvelope,
  getStaffMemberSettings as universalGetStaffMemberSettings,
  updateStaffMemberSettings as universalUpdateStaffMemberSettings,
} from './bookings-staff-settings-v1-staff-member-settings-staff-member-settings.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/bookings' };

export function getStaffMemberSettings(
  httpClient: HttpClient
): GetStaffMemberSettingsSignature {
  return () =>
    universalGetStaffMemberSettings(
      // @ts-ignore
      { httpClient }
    );
}

interface GetStaffMemberSettingsSignature {
  /**
   * Retrieves the current privacy settings for staff member contact information.
   *
   * Use this method to check whether the `email` and `phone` fields in [staff member objects](https://dev.wix.com/docs/api-reference/business-solutions/bookings/staff-members/staff-members/staff-member-object) are accessible in API responses and on the live site.
   * @returns Retrieved staff member settings.
   */
  (): Promise<StaffMemberSettings>;
}

export function updateStaffMemberSettings(
  httpClient: HttpClient
): UpdateStaffMemberSettingsSignature {
  return (
    staffMemberSettings: NonNullablePaths<StaffMemberSettings, `revision`, 2>
  ) =>
    universalUpdateStaffMemberSettings(
      staffMemberSettings,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateStaffMemberSettingsSignature {
  /**
   * Updates privacy settings for staff member contact information.
   *
   * Each time the settings are updated, `revision` increments by 1.
   * The current `revision` must be passed when updating the settings.
   * This ensures you're working with the latest settings and prevents unintended overwrites.
   *
   * Changes to these settings take effect immediately and affect both API responses and live site display.
   * When you enable `contactInformation.publiclyAccessible`, the `email` and `phone` fields in [staff member objects](https://dev.wix.com/docs/api-reference/business-solutions/bookings/staff-members/staff-members/staff-member-object) include actual contact information, and this information is displayed on the live site.
   * When you disable it, these fields are empty in API responses if the calling [identity](https://dev.wix.com/docs/build-apps/develop-your-app/access/about-identities) doesn't have sufficient permissions, and contact information isn't displayed on the live site.
   *
   * Identities with appropriate permissions can access staff contact information in API responses regardless of this setting.
   * @param - Staff member settings to update, may be partial.
   * @returns Updated staff member settings.
   */
  (
    staffMemberSettings: NonNullablePaths<StaffMemberSettings, `revision`, 2>
  ): Promise<StaffMemberSettings>;
}

export const onStaffMemberSettingsUpdated = /*#__PURE__*/ EventDefinition(
  'wix.bookings.staff_settings.v1.staff_member_settings_updated',
  true,
  (event: StaffMemberSettingsUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
            { path: 'modifiedFields.createdDate' },
            { path: 'modifiedFields.updatedDate' },
          ],
        },
      ])
    )
)<StaffMemberSettingsUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  BaseEventMetadata,
  ContactInformation,
  DomainEvent,
  DomainEventBodyOneOf,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  ExtendedFields,
  GetStaffMemberSettingsRequest,
  GetStaffMemberSettingsResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  MessageEnvelope,
  RestoreInfo,
  StaffMemberSettings,
  StaffMemberSettingsUpdatedEnvelope,
  StaffProperties,
  UpdateStaffMemberSettingsRequest,
  UpdateStaffMemberSettingsResponse,
  WebhookIdentityType,
} from './bookings-staff-settings-v1-staff-member-settings-staff-member-settings.universal.js';
